import {
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useArticleTranslationPublish } from 'articleEditor/hooks/useArticleTranslationPublish';
import { LanguageId } from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

type ArticleTranslationPublishModalProps = {
  articleId: string;
  articleTitle: string;
  languageId: LanguageId;
  languageName: string;
};

export function ArticleTranslationPublishModal({
  articleId,
  articleTitle,
  languageId,
  languageName,
}: ArticleTranslationPublishModalProps): JSX.Element {
  const { successNotify, errorNotify } = useNotification();
  const [publishFn, { loading }] = useArticleTranslationPublish({
    articleId,
    languageId,
  });
  const { hideModal } = useModal();

  const publish = useCallback(() => {
    publishFn()
      .then((result) => {
        if (result.errors?.length) {
          throw new Error(result.errors[0].message);
        }
        successNotify({
          title: 'Success',
          text: `"${articleTitle}" (${languageName}) has been published`,
        });
        hideModal();
      })
      .catch(() => {
        errorNotify({
          title: 'Error',
          text: `"${articleTitle}" (${languageName}) could not be published. Please try again or contact support.`,
        });
      });
  }, [
    publishFn,
    articleTitle,
    languageName,
    successNotify,
    hideModal,
    errorNotify,
  ]);

  return (
    <ModalWrapper>
      <ModalHead title="Publish" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to publish{' '}
          <strong>&#34;{articleTitle}&#34;</strong> in {languageName}?
        </Text.Paragraph>
      </ModalBody>
      <ModalFoot>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button isLoading={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button isLoading={loading} onClick={publish}>
            {loading ? 'Publishing...' : 'Publish'}
          </Button>
        </FlexBox>
      </ModalFoot>
    </ModalWrapper>
  );
}
